@if (buckets$ | async; as buckets) {
  @if (!isMultiple) {
    <!-- Width is owned by the dialog container (panel/shell), not the form: the form fills
         whatever definite width its host provides. A self-sized form here has been wrong twice —
         shrink-wrap resize while typing (#859), then a viewport-based width that overflowed the
         632px dialog shell and got clipped (right column + Update button cut). -->
    <form
      class="edit-transaction flex min-h-0 w-full flex-1 flex-col"
      data-testid="edit-transaction-form"
      (ngSubmit)="submitUpdate(transactionModel)"
      autocomplete="off"
      #editTransactionForm="ngForm"
    >
      <div
        class="edit-transaction-hero flex flex-col items-center gap-2 px-3 pb-4 pt-1"
        data-testid="edit-transaction-hero"
      >
        @if (merchantLogoUrl(); as logoUrl) {
          @if (!logoFailed(logoUrl)) {
            <img
              class="h-20 w-20 rounded-full object-cover"
              [src]="logoUrl"
              [alt]="merchantDisplayName() + ' logo'"
              loading="lazy"
              (error)="onLogoError(logoUrl)"
            />
          } @else {
            <span
              class="flex h-20 w-20 items-center justify-center rounded-full bg-surface-tint text-title-sm font-bold text-brand-primary-strong"
              aria-hidden="true"
            >
              {{ merchantInitial(merchantDisplayName()) }}
            </span>
          }
        } @else {
          <span
            class="flex h-20 w-20 items-center justify-center rounded-full bg-surface-tint text-title-sm font-bold text-brand-primary-strong"
            aria-hidden="true"
          >
            {{ merchantInitial(merchantDisplayName()) }}
          </span>
        }
        <p class="m-0 text-body-md font-medium text-text-title">{{ merchantDisplayName() }}</p>
        <p
          class="edit-transaction-hero__amount m-0 text-title-sm font-bold tabular-nums"
          [class.text-text-title]="!isIncome()"
          [class.text-brand-primary-strong]="isIncome()"
        >
          {{ isIncome() ? '+' : '' }}{{ transactionModel.amount | currency }} USD
        </p>
      </div>
      <!-- Hero (logo, merchant, amount) sits under the title on mobile and desktop.
           Budget / Date / Label are icon rows under the hero. -->
      <div
        class="edit-transaction-fields mb-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-y-contain p-1"
      >
        @if (!transactionModel.deleted && !transactionModel.parent?.deleted && (transactionModel.splitMismatch ?? transactionModel.parent?.splitMismatch); as mismatch) {
          <div class="rounded-md border border-border bg-surface-tint p-3 text-body-sm text-text-title" data-testid="split-mismatch-warning">
            <p class="m-0">
              Your splits are {{ (mismatch.remaining < 0 ? -mismatch.remaining : mismatch.remaining) | currency }} off from the bank amount.
            </p>
            @if (!transactionModel.showSplit) {
              <button appUiButton appearance="ghost" size="compact" type="button" (click)="startSplit()">Edit splits to fix</button>
            } @else {
              <p class="m-0 text-text-muted">Adjust your splits below to match the bank amount.</p>
            }
          </div>
        }
        <div class="edit-transaction-budget-block grid min-w-0 gap-2">
          <app-edit-transaction-row icon="wallet" label="Budget">
            <div rowAction class="edit-transaction-split-actions flex items-center">
              @if (!transactionModel.showSplit && !transactionModel.parent) {
                <button
                  class="edit-transaction-split-button"
                  data-testid="edit-transaction-split"
                  appUiButton
                  appearance="solid"
                  tone="accent"
                  size="compact"
                  type="button"
                  (click)="startSplit()"
                >
                  <app-ui-icon name="arrow-up-down" class="!h-4 !w-4" />
                  <span>Split</span>
                </button>
              }
              @if (!transactionModel.showSplit && transactionModel.parent) {
                <button
                  class="edit-transaction-edit-split-button"
                  data-testid="edit-transaction-edit-split"
                  appUiButton
                  appearance="solid"
                  tone="accent"
                  size="compact"
                  type="button"
                  (click)="startSplit()"
                >
                  <app-ui-icon name="arrow-up-down" class="!h-4 !w-4" />
                  <span>Modify split</span>
                </button>
              }
              @if (transactionModel.showSplit) {
                <button
                  data-testid="edit-transaction-collapse-split"
                  appUiButton
                  appearance="solid"
                  tone="accent"
                  size="compact"
                  type="button"
                  (click)="collapseSplit()"
                >
                  Collapse
                </button>
              }
            </div>
            @if (!transactionModel.showSplit) {
              @if (suggestedBucketPreselected) {
                <span class="sr-only" aria-live="polite">Suggested budget pre-selected — Update accepts it.</span>
              }
              <app-select-bucket-typeahead
                [buckets]="buckets"
                [selectedBucket]="selectedBucket"
                (selectedBucketChange)="onSelectedBucketChange($event)"
                [suggested]="suggestedBucketPreselected"
                hideLabel
              />
            }
          </app-edit-transaction-row>
          @if (transactionModel.showSplit) {
            <section
              class="grid gap-3"
              aria-label="Split transaction"
              data-testid="split-transaction-section"
            >
              <app-split-transaction
                [(transaction)]="transactionModel"
                [buckets]="buckets"
                embedded
              />
            </section>
          }
        </div>
        @if (syncedFinancialFactsLocked && transactionModel.parent) {
          <app-edit-transaction-row icon="calendar" label="Date">
            <p class="m-0 text-body-md text-text-muted">{{ transactionModel.date | date: 'mediumDate' }}</p>
          </app-edit-transaction-row>
        } @else {
          <app-edit-transaction-row icon="calendar" label="Date">
            <app-ui-field label="Date" inputId="edit-transaction-date" hideLabel [errorLines]="0">
              <app-ui-date-picker
                id="edit-transaction-date"
                inputId="edit-transaction-date"
                class="w-full max-w-full"
                [(ngModel)]="transactionModel.date"
                name="date"
                required
              />
            </app-ui-field>
          </app-edit-transaction-row>
        }
        <app-edit-transaction-row icon="shopping-bag" label="Label">
          <app-label-typeahead hideLabel [(selectedLabelIds)]="transactionModel.labelIds" />
        </app-edit-transaction-row>
        <div class="edit-transaction-facts flex min-w-0 flex-col gap-4">
        @if (!syncedFinancialFactsLocked) {
          <app-ui-field label="Merchant name" inputId="edit-transaction-merchant" [errorLines]="0">
            <input
              id="edit-transaction-merchant"
              appUiInput
              class="text-body-sm"
              type="text"
              [(ngModel)]="transactionModel.merchant"
              name="merchant"
              [disabled]="syncedFinancialFactsLocked"
              [attr.disabled]="syncedFinancialFactsLocked ? true : null"
              required
            />
          </app-ui-field>
          <app-ui-field label="Amount" inputId="edit-transaction-amount" [errorLines]="0">
            <app-ui-currency-input
              inputId="edit-transaction-amount"
              name="amount"
              min="0.01"
              required
              [disabled]="!!transactionModel.parent || syncedFinancialFactsLocked"
              [(ngModel)]="transactionModel.amount"
            />
          </app-ui-field>
          <app-ui-radio-group
            legend="Transaction type"
            name="type"
            [options]="transactionTypeOptions"
            [(ngModel)]="transactionModel.type"
            [disabled]="!!transactionModel.parent"
            required
          />
        }
        </div>
        <div class="edit-transaction-actions-fields flex min-w-0 flex-col gap-4">
        <app-ui-field label="Description" inputId="edit-transaction-description" [errorLines]="0">
          <textarea
            id="edit-transaction-description"
            appUiInput
            class="text-body-sm"
            rows="3"
            placeholder="More information about this transaction..."
            [(ngModel)]="transactionModel.description"
            name="description"
          ></textarea>
        </app-ui-field>
        </div>
        @if (bankImportTransaction(); as bankTransaction) {
          <section
            class="edit-transaction-bank-details rounded-md bg-surface-muted p-3 md:col-span-2"
            aria-label="From your bank"
            data-testid="edit-transaction-bank-details"
          >
            <h4 class="mb-3 text-label-sm font-semibold uppercase tracking-[0.08em] text-text-muted">
              From your bank
            </h4>
            <div class="grid grid-cols-2 gap-4 items-start">
              @if (bankPlaidAccount(bankTransaction); as plaidAccount) {
                <app-ui-field label="Account" [errorLines]="0">
                  <span class="inline-flex min-w-0 items-center gap-1.5 text-body-sm text-text">
                    @if (bankInstitutionLogoSrc(plaidAccount); as logoSrc) {
                      @if (!logoFailed(logoSrc)) {
                        <img
                          class="h-4 w-4 shrink-0 rounded-sm object-contain"
                          [src]="logoSrc"
                          [alt]="
                            plaidAccount.institution?.name
                              ? plaidAccount.institution.name + ' logo'
                              : 'Institution logo'
                          "
                          loading="lazy"
                          (error)="onLogoError(logoSrc)"
                        />
                      }
                    }
                    <span>{{ bankAccountDisplayName(plaidAccount) }}</span>
                  </span>
                </app-ui-field>
              }
              @if (bankTransaction.bankMeta?.sourceDate; as sourceDate) {
                <app-ui-field label="Bank date" [errorLines]="0">
                  <p class="m-0 text-body-sm text-text-muted">{{ sourceDate | date: 'mediumDate' }}</p>
                </app-ui-field>
              }
              @if (bankStatementDescription(bankTransaction); as statementDescription) {
                <app-ui-field class="col-span-2" label="Statement Description" [errorLines]="0">
                  <p class="break-words font-mono text-body-sm leading-snug text-text-muted">
                    {{ statementDescription }}
                  </p>
                </app-ui-field>
              }
            </div>
          </section>
        }
      </div>
      <div class="edit-transaction-actions flex shrink-0 items-center gap-2 border-t border-border bg-surface-elevated p-1 pt-2">
        @if (!transactionModel.parent && !transactionModel.deleted) {
          <button
            appUiButton
            appearance="ghost"
            tone="danger"
            class="edit-transaction-delete"
            type="button"
            [disabled]="loading"
            (click)="firstTransaction ? deleteSingleTransaction(firstTransaction) : null"
          >
            <span class="edit-transaction-action-icon" aria-hidden="true">×</span>
            Delete
          </button>
        }
        @if (transactionModel.deleted) {
          <button
            appUiButton
            appearance="ghost"
            tone="neutral"
            type="button"
            (click)="restoreTransactions(transactions())"
          >
            <span class="edit-transaction-action-icon" aria-hidden="true">↺</span>
            Restore
          </button>
        }
        <div class="ml-auto"></div>
        <button appUiButton appearance="outline" tone="neutral" type="button" [disabled]="loading" (click)="cancel()">
          Cancel
        </button>
        @if (!transactionModel.deleted) {
          <button
            appUiButton
            type="submit"
            class="edit-transaction-save"
            data-testid="edit-transaction-update"
            [disabled]="!editTransactionForm.form.valid || splitInvalid() || loading || !hasChanges(!!editTransactionForm.form.dirty)"
          >
            Update
          </button>
        }
      </div>
    </form>
  } @else {
    <form
      class="edit-transactions flex min-h-0 w-full flex-1 flex-col"
      data-testid="edit-transactions-form"
      (ngSubmit)="submitMultiUpdate(multi)"
      autocomplete="off"
      #editMultipleForm="ngForm"
    >
      <div class="edit-transaction-fields mb-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-y-contain p-1">
        @if (hasPlaidSyncedTransactionSelection) {
          <div
            id="edit-multiple-transactions-financial-facts-lock"
            class="rounded-md border border-border-muted bg-surface-muted px-3 py-2"
            data-testid="edit-multiple-transactions-financial-facts-lock"
          >
            <p class="m-0 text-body-sm font-semibold text-text">Merchant editing unavailable</p>
            <p class="mb-0 mt-0.5 text-body-sm text-text-muted">
              At least one selected transaction is synced from your bank, so merchant names will stay unchanged.
            </p>
          </div>
        } @else {
          <app-ui-field label="Merchant name" inputId="edit-multiple-transactions-merchant" [errorLines]="0">
            <input
              id="edit-multiple-transactions-merchant"
              appUiInput
              type="text"
              [(ngModel)]="multi.merchant"
              name="merchant"
            />
          </app-ui-field>
        }
        <app-select-bucket-typeahead
          [buckets]="buckets"
          [(selectedBucket)]="selectedBucket"
        />
        <app-ui-field label="Description" inputId="edit-multiple-transactions-description" [errorLines]="0">
          <textarea
            id="edit-multiple-transactions-description"
            appUiInput
            rows="3"
            placeholder="More information about this transaction..."
            [(ngModel)]="multi.description"
            name="description"
          ></textarea>
        </app-ui-field>
        <app-label-typeahead [(selectedLabelIds)]="multi.labelIds" />
      </div>
      <div class="edit-transaction-actions flex shrink-0 items-center gap-2 border-t border-border bg-surface-elevated p-1 pt-2">
        @if (canDelete(transactions())) {
          <button
            appUiButton
            appearance="ghost"
            tone="danger"
            class="edit-transaction-delete"
            type="button"
            [disabled]="loading"
            (click)="deleteTransactions(transactions())"
          >
            <span class="edit-transaction-action-icon" aria-hidden="true">×</span>
            Delete
          </button>
        }
        @if (canRestore(transactions())) {
          <button
            appUiButton
            appearance="ghost"
            tone="neutral"
            type="button"
            (click)="restoreTransactions(transactions())"
          >
            <span class="edit-transaction-action-icon" aria-hidden="true">↺</span>
            Restore
          </button>
        }
        <div class="ml-auto"></div>
        <button appUiButton appearance="outline" tone="neutral" type="button" [disabled]="loading" (click)="cancel()">
          Cancel
        </button>
        <button
          appUiButton
          type="submit"
          class="edit-transaction-save"
          data-testid="edit-transactions-update"
          [disabled]="!editMultipleForm.form.valid || loading"
        >
          Update
        </button>
      </div>
    </form>
  }
}
